import { Box, Typography, Chip, Tooltip, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

import { ChainUser } from './types';
import { ChainLevelView } from './ChainLevelView';

interface Props {
    user: ChainUser;
    currency: string;
    index: number;
}

const formatDate = (dateStr: string) => {
    try {
        const d = new Date(dateStr.replace(' ', 'T'));
        return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch {
        return dateStr;
    }
};

export const ChainUserCard = ({ user, currency, index }: Props) => {
    const theme = useTheme();
    const primaryColor = theme.palette.primary.main;

    return (
        <Box mb={3}>
            {/* ── Row label ── */}
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: 'primary.main', whiteSpace: 'nowrap' }}
                >
                    Цепь {index + 1}
                </Typography>

                <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                    {user.login}
                </Typography>

                {user.clones > 0 && (
                    <Tooltip title={`Клонов: ${user.clones}`} placement="top">
                        <Chip
                            label={`×${user.clones}`}
                            size="small"
                            sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: 'rgba(255, 107, 43, 0.15)',
                                color: '#FF6B2B',
                                border: '1px solid #FF6B2B50',
                            }}
                        />
                    </Tooltip>
                )}

                <Typography variant="caption" color="text.disabled">
                    {formatDate(user.created_at)}
                </Typography>
            </Box>

            {/* ── Swiper row of level cards ── */}
            <Box
                sx={{
                    position: 'relative',
                    // horizontal padding gives space for nav arrows
                    px: '6px',

                    '--swiper-theme-color': primaryColor,
                    '--swiper-navigation-size': '20px',
                    '--swiper-navigation-color': primaryColor,

                    '& .swiper-button-prev, & .swiper-button-next': {
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: 'background.paper',
                        border: `1px solid ${primaryColor}60`,
                        boxShadow: 1,
                        '&::after': { fontSize: '11px', fontWeight: 900 },
                        '&.swiper-button-disabled': { opacity: 0.25 },
                    },
                    '& .swiper-button-prev': { left: 0 },
                    '& .swiper-button-next': { right: 0 },
                } as any}
            >
                <Swiper
                    modules={[Navigation]}
                    navigation
                    slidesPerView="auto"
                    spaceBetween={12}
                    breakpoints={{
                        0: { slidesPerView: 1.2 },
                        768: { slidesPerView: 2.2 },
                        1200: { slidesPerView: 4.2 },
                        1600: { slidesPerView: 5.2 },
                    }}
                >
                    {user.chain.map((level, i) => (
                        <SwiperSlide key={i}>
                            <Paper
                                elevation={0}
                                sx={{
                                    bgcolor: 'rgba(74, 58, 132, 0.1)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    p: 1.5,
                                    height: '100%',
                                    width: '100%'
                                }}
                            >
                                <ChainLevelView level={level} currency={currency} />
                            </Paper>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </Box>
        </Box>
    );
};